import {
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  DarkMode as DarkModeIcon,
  Download as DownloadIcon,
  Error as ErrorIcon,
  Instagram as InstagramIcon,
  LightMode as LightModeIcon,
  Message as MessageIcon,
  WhatsApp as WhatsAppIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  LinearProgress,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import axios from "axios";
import React, { useRef, useState, useEffect } from "react";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const MESSAGE_CHAR_LIMIT = 72;

const TextingStoryVideoCreator = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [suggestion, setSuggestion] = useState("");
  const [appType, setAppType] = useState("instagram");
  const [theme, setTheme] = useState("dark");
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const progressTimerRef = useRef(null);

  // Progress timer effect
  useEffect(() => {
    if (loading) {
      setProgress(0);
      const startTime = Date.now();
      // New progress calculation: approaches 100% asymptotically.
      // It gets to ~95% in 20 seconds, then slows down, never reaching 100%.
      const K = 0.15; // Rate constant for progress

      progressTimerRef.current = setInterval(() => {
        const elapsedSeconds = (Date.now() - startTime) / 1000;
        const progressPercent = 100 * (1 - Math.exp(-K * elapsedSeconds));

        setProgress(progressPercent);
      }, 100); // Update every 100ms for smooth animation

      return () => {
        if (progressTimerRef.current) {
          clearInterval(progressTimerRef.current);
        }
      };
    } else {
      // Clear timer and reset progress when not loading
      if (progressTimerRef.current) {
        clearInterval(progressTimerRef.current);
      }
      setProgress(0);
    }
  }, [loading]);


  // Available themes
  const availableThemes = [
    { key: "dark", name: "Dark", icon: <DarkModeIcon />, available: true },
    { key: "light", name: "Light", icon: <LightModeIcon />, available: false },
  ];

  // Hardcoded app availability - Instagram available, others coming soon
  const availableApps = {
    instagram: { available: true, name: "Instagram" },
    imessage: { available: false, name: "iMessage" },
    whatsapp: { available: false, name: "WhatsApp" },
  };

  const validateAndSetFile = (file) => {
    if (!file) return false;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      setError("Please select an image file");
      return false;
    }

    // Validate file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      setError("Image file is too large. Maximum size is 10MB");
      return false;
    }

    setSelectedFile(file);
    setError("");

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target.result);
    };
    reader.readAsDataURL(file);
    return true;
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    validateAndSetFile(file);
  };

  const handleImagePreviewClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragEnter = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    // Only set dragOver to false if we're leaving the drop zone completely
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setIsDragOver(false);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);

    const files = event.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      validateAndSetFile(file);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSuggestionChange = (event) => {
    const value = event.target.value;
    setSuggestion(value);

    // Clear error if user is typing
    if (error && value.trim()) {
      setError("");
    }
  };

  const validateForm = () => {
    if (!selectedFile) {
      setError("Please select an image file");
      return false;
    }

    if (!suggestion.trim()) {
      setError("Please enter a message suggestion");
      return false;
    }

    if (suggestion.length > MESSAGE_CHAR_LIMIT) {
      setError(
        `Message suggestion is too long. Maximum ${MESSAGE_CHAR_LIMIT} characters allowed.`
      );
      return false;
    }

    if (!availableApps[appType]?.available) {
      setError(
        `${
          availableApps[appType]?.name || appType
        } template is not available yet`
      );
      return false;
    }

    const selectedTheme = availableThemes.find(t => t.key === theme);
    if (!selectedTheme?.available) {
      setError(
        `${selectedTheme?.name || theme} theme is not available yet`
      );
      return false;
    }

    return true;
  };

  const handleGenerate = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError("");

    try {
      const formData = new FormData();
      formData.append("image", selectedFile);
      formData.append("suggestion", suggestion.trim());
      formData.append("app_type", appType);
      formData.append("theme", theme);

      const response = await axios.post(
        "/marketing/create-texting-story/",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          responseType: "blob", // Important for handling video file
        }
      );

      // Get the video blob
      const blob = response.data;

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `texting-story-${appType}-${theme}-${Date.now()}.mp4`;
      document.body.appendChild(a);
      a.click();

      // Cleanup
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      setSuccess(true);
    } catch (err) {
      // Handle axios errors
      if (err.response) {
        // Server responded with error status
        setError(`Server error: ${err.response.status}`);
      } else if (err.request) {
        // Network error
        setError("Network error: Please check your connection");
      } else {
        // Other error
        setError(err.message || "An error occurred while generating the video");
      }
    } finally {
      setLoading(false);
    }
  };

  const getAppIcon = (app) => {
    switch (app) {
      case "instagram":
        return <InstagramIcon />;
      case "imessage":
        return <MessageIcon />;
      case "whatsapp":
        return <WhatsAppIcon />;
      default:
        return <MessageIcon />;
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        overflowX: "hidden",
        pb: { xs: 2, md: 4 }, // Add bottom padding to prevent cut-off
        px: 1, // Add some horizontal padding for mobile
      }}
    >
      <Typography variant="body1" color="#888" sx={{ mb: { xs: 2, md: 4 } }}>
        Create a Smoothspeak video clip to use in your texting story by
        uploading an image and adding your message suggestion.
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: { xs: 2, md: 4 },
          mb: { xs: 2, md: 4 },
          alignItems: "stretch",
        }}
      >
        {/* Left Column: Upload Image */}
        <Box sx={{ flex: 0.8, display: "flex", flexDirection: "column" }}>
          <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1 }}>
            <Typography 
              variant="h6" 
              gutterBottom 
              sx={{ 
                color: "#FFFFFF",
                fontSize: { xs: "1rem", md: "1.25rem" }
              }}
            >
              Upload Screenshot
            </Typography>
            <Box
              onClick={handleImagePreviewClick}
              onDragOver={handleDragOver}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              sx={{
                flexGrow: 1,
                minHeight: { xs: 200, md: "auto" },
                border: isDragOver
                  ? "2px dashed #E53935"
                  : "2px dashed #F53D3B",
                borderRadius: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                overflow: "hidden",
                backgroundColor: isDragOver ? "#3A3A3C" : "#2C2C2E",
                cursor: "pointer",
                transition: "all 0.3s ease",
                "&:hover": {
                  borderColor: "#E53935",
                  backgroundColor: "#3A3A3C",
                },
                "& img": {
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                },
              }}
            >
              {imagePreview ? (
                <>
                  <img src={imagePreview} alt="Preview" />
                  <IconButton
                    sx={{
                      position: "absolute",
                      top: 5,
                      right: 5,
                      bgcolor: "rgba(0,0,0,0.7)",
                      color: "white",
                      "&:hover": { bgcolor: "rgba(0,0,0,0.9)" },
                    }}
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveFile();
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </>
              ) : (
                <Box sx={{ textAlign: "center", p: 2 }}>
                  <CloudUploadIcon
                    sx={{ fontSize: 48, color: "#F53D3B", mb: 1 }}
                  />
                  <Typography
                    variant="body1"
                    color="#888"
                    sx={{ mb: 2, fontWeight: "bold" }}
                  >
                    {isDragOver
                      ? "Drop image here"
                      : "Click or drag to upload the screenshot of the messages Smoothspeak will respond to"}
                  </Typography>
                  <Typography variant="body2" color="#888" sx={{ mb: 1 }}>
                    Supported formats: JPG and PNG
                  </Typography>
                </Box>
              )}
            </Box>

            <Box
              sx={{ pt: 2, minHeight: "52px" /* So the layout doesn't jump */ }}
            >
              {selectedFile && (
                <Chip
                  label={`${selectedFile.name} (${(
                    selectedFile.size /
                    (1024 * 1024)
                  ).toFixed(2)}MB)`}
                  variant="outlined"
                  sx={{
                    borderColor: "#F53D3B",
                    color: "#FFFFFF",
                    backgroundColor: "rgba(245, 61, 59, 0.1)",
                    maxWidth: "100%",
                    "& .MuiChip-label": {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    },
                  }}
                />
              )}
            </Box>
          </Box>
          <VisuallyHiddenInput
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
          />
        </Box>

        {/* Right Column: Steps 2-4 */}
        <Box sx={{ flex: 1.2, display: "flex", flexDirection: "column" }}>
          {/* Message Suggestion Section */}
          <Box sx={{ mb: { xs: 1, md: 2 } }}>
            <Typography 
              variant="h6" 
              gutterBottom 
              sx={{ 
                color: "#FFFFFF",
                fontSize: { xs: "1rem", md: "1.25rem" }
              }}
            >
              Enter Message Suggestion
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Message Suggestion"
              placeholder="e.g., That sounds like fun! What time?"
              value={suggestion}
              onChange={handleSuggestionChange}
              helperText={`${suggestion.length}/${MESSAGE_CHAR_LIMIT} characters`}
              error={suggestion.length > MESSAGE_CHAR_LIMIT}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#2C2C2E",
                  color: "#FFFFFF",
                  "& fieldset": {
                    borderColor: "#444",
                  },
                  "&:hover fieldset": {
                    borderColor: "#F53D3B",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#F53D3B",
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "#888",
                  "&.Mui-focused": {
                    color: "#F53D3B",
                  },
                },
                "& .MuiFormHelperText-root": {
                  color: "#888",
                },
              }}
            />
          </Box>

          <Divider sx={{ my: { xs: 1, md: 2 }, borderColor: "#333" }} />

          {/* App Type Selection */}
          <Box sx={{ mb: { xs: 1, md: 2 } }}>
            <Typography 
              variant="h6" 
              gutterBottom 
              sx={{ 
                color: "#FFFFFF",
                fontSize: { xs: "1rem", md: "1.25rem" }
              }}
            >
              Select App Type
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {Object.entries(availableApps).map(([key, app]) => (
                <Box
                  key={key}
                  sx={{ position: "relative", display: "inline-block" }}
                >
                  <Chip
                    icon={getAppIcon(key)}
                    label={app.name}
                    clickable={app.available}
                    onClick={() => app.available && setAppType(key)}
                    sx={{
                      backgroundColor:
                        appType === key
                          ? "#F53D3B"
                          : app.available
                          ? "#4CAF50"
                          : "#2C2C2E",
                      color: "#FFFFFF",
                      borderColor:
                        appType === key
                          ? "#F53D3B"
                          : app.available
                          ? "#4CAF50"
                          : "#444",
                      border:
                        appType === key ? "2px solid #F53D3B" : "1px solid",
                      cursor: app.available ? "pointer" : "not-allowed",
                      transition: "all 0.3s ease",
                      opacity: app.available ? 1 : 0.6,
                      height: { xs: "36px", md: "48px" },
                      fontSize: { xs: "0.875rem", md: "1rem" },
                      padding: { xs: "8px 12px", md: "12px 16px" },
                      "& .MuiChip-label": {
                        fontSize: { xs: "0.875rem", md: "1rem" },
                        fontWeight: 500,
                      },
                      "& .MuiChip-icon": {
                        fontSize: { xs: "1rem", md: "1.2rem" },
                        color: "#FFFFFF",
                      },
                      "&:hover": app.available
                        ? {
                            backgroundColor:
                              appType === key ? "#E53935" : "#66BB6A",
                            transform: "scale(1.05)",
                          }
                        : {},
                    }}
                  />
                  {!app.available && (
                    <Chip
                      size="small"
                      label="Coming Soon"
                      sx={{
                        position: "absolute",
                        top: -8,
                        right: -8,
                        backgroundColor: "#FF9800",
                        color: "#FFFFFF",
                        fontSize: { xs: "0.6rem", md: "0.7rem" },
                        height: { xs: "16px", md: "18px" },
                        "& .MuiChip-label": {
                          px: { xs: 0.5, md: 1 },
                          fontSize: { xs: "0.6rem", md: "0.7rem" },
                        },
                      }}
                    />
                  )}
                </Box>
              ))}
            </Box>
          </Box>

          <Divider sx={{ my: { xs: 1, md: 2 }, borderColor: "#333" }} />

          {/* Theme Selection */}
          <Box>
            <Typography 
              variant="h6" 
              gutterBottom 
              sx={{ 
                color: "#FFFFFF",
                fontSize: { xs: "1rem", md: "1.25rem" }
              }}
            >
              Select Theme
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {availableThemes.map((themeOption) => (
                <Box
                  key={themeOption.key}
                  sx={{ position: "relative", display: "inline-block" }}
                >
                  <Chip
                    icon={themeOption.icon}
                    label={themeOption.name}
                    clickable={themeOption.available}
                    onClick={() => themeOption.available && setTheme(themeOption.key)}
                    sx={{
                      backgroundColor:
                        theme === themeOption.key
                          ? "#F53D3B"
                          : themeOption.available
                          ? "#2C2C2E"
                          : "#2C2C2E",
                      color: "#FFFFFF",
                      borderColor:
                        theme === themeOption.key
                          ? "#F53D3B"
                          : themeOption.available
                          ? "#444"
                          : "#444",
                      border:
                        theme === themeOption.key
                          ? "2px solid #F53D3B"
                          : "1px solid",
                      cursor: themeOption.available ? "pointer" : "not-allowed",
                      transition: "all 0.3s ease",
                      opacity: themeOption.available ? 1 : 0.6,
                      height: { xs: "36px", md: "48px" },
                      fontSize: { xs: "0.875rem", md: "1rem" },
                      padding: { xs: "8px 12px", md: "12px 16px" },
                      "& .MuiChip-label": {
                        fontSize: { xs: "0.875rem", md: "1rem" },
                        fontWeight: 500,
                      },
                      "& .MuiChip-icon": {
                        fontSize: { xs: "1rem", md: "1.2rem" },
                        color: "#FFFFFF",
                      },
                      "&:hover": themeOption.available
                        ? {
                            backgroundColor:
                              theme === themeOption.key ? "#E53935" : "#3A3A3C",
                            transform: "scale(1.05)",
                          }
                        : {},
                    }}
                  />
                  {!themeOption.available && (
                    <Chip
                      size="small"
                      label="Coming Soon"
                      sx={{
                        position: "absolute",
                        top: -8,
                        right: -8,
                        backgroundColor: "#FF9800",
                        color: "#FFFFFF",
                        fontSize: { xs: "0.6rem", md: "0.7rem" },
                        height: { xs: "16px", md: "18px" },
                        "& .MuiChip-label": {
                          px: { xs: 0.5, md: 1 },
                          fontSize: { xs: "0.6rem", md: "0.7rem" },
                        },
                      }}
                    />
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>

      <Divider sx={{ my: { xs: 2, md: 3 }, borderColor: "#333" }} />

      {/* Generate Button */}
      <Box sx={{ textAlign: "center", mb: { xs: 1, md: 2 } }}>
        <Button
          variant="contained"
          size="large"
          onClick={handleGenerate}
          disabled={loading || !selectedFile || !suggestion.trim()}
          startIcon={
            loading ? (
              <CircularProgress size={20} sx={{ color: "#FFFFFF" }} />
            ) : (
              <DownloadIcon />
            )
          }
          sx={{
            px: 6,
            py: 2,
            minHeight: "56px", // Ensure consistent height
            backgroundColor: "#F53D3B",
            color: "#FFFFFF",
            "&:hover": {
              backgroundColor: "#E53935",
            },
            "&:disabled": {
              backgroundColor: "#555",
              color: "#888",
            },
          }}
        >
          {loading ? "Generating Video..." : "Generate Video"}
        </Button>
      </Box>

      {/* Loading Progress */}
      {loading && (
        <Box sx={{ mb: { xs: 2, md: 4 } }}>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              backgroundColor: "#333",
              "& .MuiLinearProgress-bar": {
                backgroundColor: "#F53D3B",
              },
            }}
          />
          <Typography
            variant="body2"
            color="#888"
            sx={{ mt: 1, textAlign: "center" }}
          >
            Creating your Smoothspeak video clip...
          </Typography>
        </Box>
      )}

      {/* Error Display */}
      {error && (
        <Alert
          severity="error"
          sx={{
            mb: { xs: 2, md: 4 },
            backgroundColor: "rgba(244, 67, 54, 0.1)",
            color: "#FFFFFF",
            borderColor: "#F44336",
            "& .MuiAlert-icon": {
              color: "#F44336",
            },
          }}
          icon={<ErrorIcon />}
        >
          {error}
        </Alert>
      )}

      {/* Success Snackbar */}
      <Snackbar
        open={success}
        autoHideDuration={6000}
        onClose={() => setSuccess(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSuccess(false)}
          severity="success"
          sx={{
            width: "100%",
            backgroundColor: "#4CAF50",
            color: "#FFFFFF",
          }}
        >
          Video generated successfully! Download should start automatically.
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TextingStoryVideoCreator;
